/**
 * System Schedule — Platform Admin, read-only.
 *
 * Answers "what does this instance run on a timer, and when did it last
 * run" without anyone having to remember cron times: the celery beat
 * roster (DB-backed, so it is the live truth for this install) plus the
 * forecast snapshot freshness per customer.
 */
import { useEffect, useState } from 'react';
import { coreAPI, SystemSchedule } from '../../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Loader2, AlertCircle, Clock, CalendarClock, RefreshCw } from 'lucide-react';

function formatDateTime(value: string | null): string {
  if (!value) return 'never';
  return new Date(value).toLocaleString();
}

function timeAgo(value: string | null, serverTime: string): string {
  if (!value) return '';
  const ms = new Date(serverTime).getTime() - new Date(value).getTime();
  if (ms < 0) return '';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function SystemScheduleTab() {
  const [data, setData] = useState<SystemSchedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    coreAPI.getSystemSchedule()
      .then(res => setData(res.data))
      .catch((err: unknown) => {
        console.error('Failed to load system schedule:', err);
        setError('Failed to load the system schedule.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span className="text-sm">{error ?? 'No schedule data available.'}</span>
        <button onClick={load} className="ml-auto text-sm underline">Retry</button>
      </div>
    );
  }

  const enabledTasks = data.periodic_tasks.filter(t => t.enabled);
  const disabledTasks = data.periodic_tasks.filter(t => !t.enabled);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Read-only. Server time: {formatDateTime(data.server_time)}
        </p>
        <button
          onClick={load}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarClock className="h-4 w-4" />
            Scheduled jobs ({enabledTasks.length} active
            {disabledTasks.length > 0 ? `, ${disabledTasks.length} disabled` : ''})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.periodic_tasks.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No scheduled jobs configured on this instance.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-2 pr-4 font-medium">Job</th>
                    <th className="py-2 pr-4 font-medium">Schedule</th>
                    <th className="py-2 pr-4 font-medium">Last run</th>
                    <th className="py-2 pr-4 font-medium text-right">Runs</th>
                    <th className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {[...enabledTasks, ...disabledTasks].map(t => (
                    <tr key={t.name} className="border-b last:border-0 align-top">
                      <td className="py-2 pr-4">
                        <div className="font-medium">{t.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{t.task}</div>
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap font-mono text-xs pt-2.5">{t.schedule}</td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        {formatDateTime(t.last_run_at)}
                        {t.last_run_at && (
                          <span className="text-xs text-muted-foreground ml-1.5">
                            {timeAgo(t.last_run_at, data.server_time)}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">{t.total_run_count.toLocaleString()}</td>
                      <td className="py-2">
                        {t.enabled
                          ? <Badge variant="outline" className="text-green-700 border-green-300 bg-green-50">active</Badge>
                          : <Badge variant="outline" className="text-muted-foreground">disabled</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Forecast snapshot freshness
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.forecast_snapshots.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">
              No forecast snapshots on this instance (snapshot serving not enabled).
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-2 pr-4 font-medium">Customer</th>
                    <th className="py-2 pr-4 font-medium">Last rebuilt</th>
                    <th className="py-2 pr-4 font-medium text-right">Rows</th>
                    <th className="py-2 pr-4 font-medium text-right">Duration</th>
                    <th className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.forecast_snapshots.map(s => (
                    <tr key={s.customer} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium">{s.customer}</td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        {formatDateTime(s.last_refreshed_at)}
                        {s.last_refreshed_at && (
                          <span className="text-xs text-muted-foreground ml-1.5">
                            {timeAgo(s.last_refreshed_at, data.server_time)}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">{s.last_row_count?.toLocaleString() ?? '—'}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">
                        {s.last_duration_seconds != null ? `${s.last_duration_seconds.toFixed(1)}s` : '—'}
                      </td>
                      <td className="py-2">
                        {s.last_status === 'ok' && <Badge variant="outline" className="text-green-700 border-green-300 bg-green-50">ok</Badge>}
                        {s.last_status === 'running' && <Badge variant="outline" className="text-blue-700 border-blue-300 bg-blue-50">running</Badge>}
                        {s.last_status === 'failed' && (
                          <Badge variant="outline" className="text-red-700 border-red-300 bg-red-50" title={s.last_error}>failed</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Middleware / ERP pipeline schedules are configured on the middleware deployment
        (not readable from Helix) and are not listed here.
      </p>
    </div>
  );
}
